import {
  ipeConclusionText, populationFacts, tieOutRows, populationPeriod, populationSamples, runCurrency,
} from '../../features/control-testing/report/population.ts';
import {
  conclusionBanner, conclusionTexts, confidencePct, headerConclusion, reviewProgress,
} from '../../features/control-testing/report/header.ts';
import { buildExhibits } from '../../features/control-testing/report/exhibits.ts';
import { blobErrorMessage, downloadWorkingPaper } from '../../features/control-testing/report/download.ts';
import React, { useState, useEffect, useCallback, useRef, createContext, useContext, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Loader2, Play, ArrowLeft, Download, RotateCcw,
  CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronUp, FileText,
  AlertOctagon, AlertCircle, Sparkles, Eye, Shield, PenLine, Stamp, Save,
  Minus, Plus, Flag,
} from 'lucide-react';
import axiosInstance from '../../api/axios';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import { MODULE_KEYS } from '../../constants/moduleAccess.ts';
import { useRequireModuleAccess } from '../../hooks/useRequireModuleAccess.ts';
import ModuleAccessDeniedDialog from '../shared/ModuleAccessDeniedDialog.tsx';
import SampleDataTable from './SampleDataTable';
import { runAttributeTest } from './runAttributeTest';
import EvidenceViewer from '../EvidenceViewer';
import { formatCalendarDate } from '../../utils/calendarDate';
import { resolveExpectedSamples } from '../test-runs/sampleRows';
import SignOffPanel from '../test-runs/SignOffPanel';
import EditableField from '../common/EditableField';
import PopulationNotRequiredPanel from './PopulationNotRequiredPanel';
import { getPopulationRequirement } from '../../features/controls/populationRequirement.ts';
import { useAuditYear } from '../../contexts/AuditYearContext.tsx';

const TestExecutionAccessContext = createContext(null);

function useTestExecutionAccessGuard() {
  const ctx = useContext(TestExecutionAccessContext);
  return ctx || { checkAccess: () => true };
}

/* ── helpers ───────────────────────────────────────────────────────────────── */
const fmt      = (v) => v ? String(v).split('T')[0] : null;
// Periods are calendar days: format them as written, not shifted into the viewer's time zone (see utils/calendarDate).
const fmtCalDate = (v) => formatCalendarDate(v) || '—';
const fmtDate  = (v) => v ? new Date(v).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const norm     = (ref) => typeof ref === 'string' ? ref.replace(/^["']+|["']+$/g, '').trim() : ref;

function parseStepResults(sampleResult) {
  try {
    const raw = sampleResult?.attributes?._step_results;
    if (!raw) return [];
    return JSON.parse(raw);
  } catch { return []; }
}

/* ── WP section wrapper ────────────────────────────────────────────────────── */
function WPSection({ numeral, title, badge, right, children, defaultOpen = true, noPad = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-300 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className="w-full flex items-center gap-3 bg-slate-700 px-4 py-2.5 text-left hover:bg-slate-800 transition-colors"
      >
        {numeral && <span className="text-[10px] font-bold text-slate-300 tracking-widest uppercase w-6 shrink-0 font-mono">{numeral}</span>}
        <span className="flex-1 text-xs font-bold text-white tracking-wide uppercase">{title}</span>
        {badge  && <span className="text-[10px] text-slate-300 shrink-0 bg-slate-600 px-2 py-0.5 rounded">{badge}</span>}
        {right  && <span className="text-[10px] text-slate-300 shrink-0">{right}</span>}
        <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={noPad ? '' : 'px-4 py-3'}>
          {children}
        </div>
      )}
    </div>
  );
}

function MetaRow({ label, val }) {
  return (
    <div className="flex items-baseline gap-2 min-w-0">
      <span className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold w-24 shrink-0">{label}</span>
      <span className="text-xs text-gray-800 truncate">{val || '—'}</span>
    </div>
  );
}

function MetaInline({ label, val }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold shrink-0">{label}:</span>
      <span className="text-xs text-gray-800">{val}</span>
    </div>
  );
}

/* ── Working paper header ───────────────────────────────────────────────────── */
// The per-attribute "Flag for re-run" control is switched off for now; set to true to bring it back.
const SHOW_RERUN_FLAG = false;

function WPHeader({ control, run, exec, confPct, running, onRerun, embedded, onRunNew, onExportPdf, exportingPdf, onExportExcel, exportingExcel, watermark, onWatermarkToggle, reviewedCount, totalSamples, preparerInfo }) {
  const [showDownloadMenu, setShowDownloadMenu] = React.useState(false);
  const periodStart = run?.testing_period_start ? fmtCalDate(run.testing_period_start) : '—';
  const periodEnd   = run?.testing_period_end   ? fmtCalDate(run.testing_period_end)   : '—';
  const runDate     = run?.created_at ? fmtDate(run.created_at) : '—';
  const badge = headerConclusion(exec, run);

  const conclusionBadge = () => {
    if (badge?.kind === 'effective')
      return <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-50 border border-green-300 px-2.5 py-1 rounded"><CheckCircle2 className="h-3.5 w-3.5" /> EFFECTIVE</span>;
    if (badge?.kind === 'not_effective')
      return <span className="inline-flex items-center gap-1 text-xs font-bold text-red-700 bg-red-50 border border-red-300 px-2.5 py-1 rounded"><XCircle className="h-3.5 w-3.5" /> NOT EFFECTIVE</span>;
    if (badge?.kind === 'review_complete')
      return <span className="inline-flex items-center gap-1 text-xs font-bold text-teal-700 bg-teal-50 border border-teal-300 px-2.5 py-1 rounded"><CheckCircle2 className="h-3.5 w-3.5" /> REVIEW COMPLETE</span>;
    if (badge?.kind === 'needs_review') {
      return <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-300 px-2.5 py-1 rounded"><AlertTriangle className="h-3.5 w-3.5" /> NEEDS REVIEW</span>;
    }
    if (badge?.kind === 'completed')
      return <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-600 bg-slate-100 border border-slate-300 px-2.5 py-1 rounded"><CheckCircle2 className="h-3.5 w-3.5" /> COMPLETED</span>;
    return null;
  };

  return (
    <div className="border border-gray-400 bg-white shadow-sm">
      {/* Top stripe */}
      <div className="bg-slate-800 px-5 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-slate-300" />
          <span className="text-xs font-bold text-white tracking-widest uppercase">Working Paper — SOX Control Test</span>
        </div>
        <div className="flex items-center gap-3">
          {control?.control_id && <span className="text-xs text-slate-300 font-mono">WP Ref: {control.control_id}-OE</span>}
          {run?.run_number != null && <span className="text-[10px] text-slate-400 font-mono bg-slate-700 px-2 py-0.5 rounded">RUN-{run.run_number}</span>}
        </div>
      </div>

      {/* Metadata grid */}
      <div className="px-5 py-3 grid grid-cols-2 gap-x-10 gap-y-1.5 border-b border-gray-200">
        <MetaRow label="Client" val="Skeptics Inc." />
        <MetaRow label="Period" val={`${periodStart} — ${periodEnd}`} />
        <MetaRow label="Control" val={[control?.control_id, control?.title || control?.control_wording_text].filter(Boolean).join(' — ')} />
        <MetaRow label="Significance" val={control?.significance} />
        <MetaRow label="Frequency" val={control?.frequency} />
      </div>

      {/* Sign block */}
      <div className="px-5 py-2 grid grid-cols-2 gap-x-10 border-b border-gray-200 bg-gray-50/60">
        <div className="flex items-center gap-3 py-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold w-24 shrink-0">Prepared by</span>
          {run?.workflow_status === 'signed_off' && run?.preparer_name ? (
            <div className="flex flex-col">
              <span className="text-xs text-gray-700 font-medium">{run.preparer_name}</span>
              <span className="text-[10px] text-gray-400">(AI-Assisted)</span>
            </div>
          ) : (
            <span className="text-xs text-gray-700">{preparerInfo || 'AI-Assisted Analysis'}</span>
          )}
          <span className="text-[10px] text-gray-400 ml-2">{runDate}</span>
        </div>
        <div className="flex items-center gap-3 py-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold w-24 shrink-0">Reviewed by</span>
          {(run?.signed_off_by || run?.reviewer_name) ? (
            <>
              <span className="text-xs text-gray-700 font-medium">{run.signed_off_by || run.reviewer_name}</span>
              {run.signed_off_at && <span className="text-[10px] text-gray-400 ml-2">{fmtDate(run.signed_off_at)}</span>}
            </>
          ) : (
            <span className="border-b border-gray-300 w-36 h-4 inline-block" />
          )}
        </div>
      </div>

      {/* Status + actions */}
      <div className="px-5 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          {conclusionBadge()}
          {exec?.total_exceptions > 0 && (
            <span className="text-xs text-red-600"><AlertOctagon className="h-3.5 w-3.5 inline mr-1" />{exec.total_exceptions} exception(s) noted</span>
          )}
          {confPct != null && (
            <span className="text-xs text-indigo-500 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" />AI confidence {confPct}%</span>
          )}
          {totalSamples > 0 && (
            <span className={`text-xs font-medium flex items-center gap-1 px-2 py-0.5 rounded-full border ${
              reviewedCount === totalSamples
                ? 'bg-green-50 border-green-300 text-green-700'
                : 'bg-gray-50 border-gray-300 text-gray-500'
            }`}>
              <CheckCircle2 className="h-3 w-3" />
              {reviewedCount} / {totalSamples} reviewed
            </span>
          )}
        </div>
        {!embedded && (
          <div className="flex items-center gap-2">
            <button
              onClick={onWatermarkToggle}
              title={watermark ? `Current: ${watermark.toUpperCase()} — click to cycle` : 'Add watermark'}
              className={`inline-flex items-center gap-1.5 text-xs border px-3 py-1.5 rounded ${
                watermark === 'draft'  ? 'border-amber-400 text-amber-700 bg-amber-50' :
                watermark === 'final'  ? 'border-green-400 text-green-700 bg-green-50' :
                'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Stamp className="h-3.5 w-3.5" />
              {watermark ? watermark.toUpperCase() : 'Watermark'}
            </button>
            <button
              onClick={onRerun} disabled={running}
              className="inline-flex items-center gap-1.5 text-xs border border-gray-300 text-gray-600 px-3 py-1.5 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
              {running ? 'Running…' : 'Re-run'}
            </button>
            <div className="relative">
              <button
                onClick={() => setShowDownloadMenu(m => !m)}
                disabled={exportingPdf || exportingExcel}
                className="inline-flex items-center gap-1.5 text-xs border border-gray-300 text-gray-600 px-3 py-1.5 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                {(exportingPdf || exportingExcel) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                {exportingPdf ? 'Generating PDF…' : exportingExcel ? 'Generating Excel…' : 'Export'}
                <span className="ml-0.5">▾</span>
              </button>
              {showDownloadMenu && (
                <div className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded shadow-lg z-50">
                  <button
                    className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                    onClick={() => { setShowDownloadMenu(false); onExportPdf(); }}
                  >PDF Working Paper</button>
                  <button
                    className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                    onClick={() => { setShowDownloadMenu(false); onExportExcel(); }}
                  >Excel Working Paper</button>
                </div>
              )}
            </div>
          </div>
        )}
        {embedded && (
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setShowDownloadMenu(m => !m)}
                disabled={exportingPdf || exportingExcel}
                className="inline-flex items-center gap-1.5 text-xs border border-gray-300 text-gray-600 px-3 py-1.5 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                {(exportingPdf || exportingExcel) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                {exportingPdf ? 'Generating PDF…' : exportingExcel ? 'Generating Excel…' : 'Download'}
                <span className="ml-0.5">▾</span>
              </button>
              {showDownloadMenu && (
                <div className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded shadow-lg z-50">
                  <button
                    className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                    onClick={() => { setShowDownloadMenu(false); onExportPdf(); }}
                  >PDF Working Paper</button>
                  <button
                    className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                    onClick={() => { setShowDownloadMenu(false); onExportExcel(); }}
                  >Excel Working Paper</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── I. Objective ───────────────────────────────────────────────────────────── */
function ObjectiveSection({ control, run }) {
  const objective  = control?.objective || control?.control_wording_text || '';
  const periodStart = run?.testing_period_start ? fmtCalDate(run.testing_period_start) : null;
  const periodEnd   = run?.testing_period_end   ? fmtCalDate(run.testing_period_end)   : null;
  const periodText  = periodStart && periodEnd ? `${periodStart} to ${periodEnd}` : 'the period under review';

  return (
    <WPSection numeral="I" title="Objective">
      <div className="space-y-3">
        <div>
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">Control Description</p>
          <p className="text-xs text-gray-700 leading-relaxed">{objective || '—'}</p>
        </div>
        <div className="pt-2 border-t border-gray-100">
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">Testing Objective</p>
          <p className="text-xs text-gray-700 leading-relaxed">
            To test the operating effectiveness of this control during the period {periodText}, by evaluating whether
            the control was applied consistently and completely in accordance with its stated design for each item in the sample.
          </p>
        </div>
        <div className="flex flex-wrap gap-x-10 gap-y-2 pt-2 border-t border-gray-100">
          <MetaInline label="Assertion(s)"      val={
            (() => {
              const assertionLabels = {
                'existence': 'Existence',
                'accuracy & valuation': 'Accuracy & Valuation',
                'occurence': 'Occurence',
                'rights & obligation': 'Rights & Obligation',
                'cut-off': 'Cut-off',
                'presentation & disclosure': 'Presentation & Disclosure',
                'completeness': 'Completeness',
              };
              const raw = control?.assertions;
              if (Array.isArray(raw) && raw.length > 0)
                return raw.map(v => assertionLabels[v] || v).join(', ');
              if (typeof raw === 'string' && raw.trim())
                return raw;
              return '—';
            })()
          } />
          <MetaInline label="Testing Approach"  val="Attribute-based sample testing" />
          <MetaInline label="External Reliance" val={control?.external_auditor_reliance ? 'Yes — reliance placed' : 'No'} />
          <MetaInline label="Control Nature"    val={control?.control_nature || '—'} />
        </div>
      </div>
    </WPSection>
  );
}

/* ── II. Population & Sample Selection ─────────────────────────────────────── */
// runCurrency and populationPeriod live in features/control-testing/report/population.ts (shared with the working
// paper); re-exported here for existing importers.
export { runCurrency, populationPeriod };

function PopulationSection({ run, planFields, evidenceMap, attributes, runId, onReload, isLocked, populationRationale }) {
  const { checkAccess: checkTestExecutionsAccess } = useTestExecutionAccessGuard();
  // The population figures, tolerances and conclusion text: features/control-testing/report/population.ts.
  const population = run?.population || {};
  const facts = populationFacts(run);
  const {
    notRequired: populationNotRequired, totalPopulation: totalPop, selectionLabel, selectionText, identifierColumn,
    isVerified, isException, filteredTotal, independentTotal,
    popFileName, proofFileName, ipeVerifiedAt, ipeSourceNote, hasIPEData, legacyIPEValidated,
  } = facts;

  // Amounts in the currency of the files (marked most in the population / evidence); plain numbers when none.
  const currency = runCurrency(run);

  // Period covered: the dates each file spans, against the testing period.
  const period = populationPeriod(run);

  // IPE modal state
  const [ipeModal, setIpeModal]             = useState(false);
  const [ipeLoading, setIpeLoading]         = useState(false);
  const [ipeError, setIpeError]             = useState(null);
  const [ipeFiles, setIpeFiles]             = useState([]);
  const [ipePopFileId, setIpePopFileId]     = useState('');
  const [ipeProofFileId, setIpeProofFileId] = useState('');
  const [ipeSourceNoteInput, setIpeSourceNoteInput] = useState('');

  // Variance explanation save state (inline, for exception status)
  const [explanationDraft, setExplanationDraft] = useState(ipeSourceNote || '');
  const [explanationSaving, setExplanationSaving] = useState(false);

  const openIpeModal = async () => {
    setIpeError(null);
    // Pre-populate population file from the run's linked population
    const popId = population?.population_file_id ? String(population.population_file_id) : '';
    setIpePopFileId(popId);
    setIpeProofFileId('');
    setIpeSourceNoteInput(ipeSourceNote || '');
    setIpeModal(true);
    try {
      const r = await axiosInstance.get(`/api/control-test-runs/${runId}/evidence`);
      const files = Array.isArray(r.data?.evidence) ? r.data.evidence
                  : Array.isArray(r.data)           ? r.data
                  : [];
      setIpeFiles(files);
    } catch {
      setIpeFiles([]);
    }
  };

  const handleIpeVerify = async () => {
    if (!runId) return;
    if (!checkTestExecutionsAccess()) return;
    if (!ipePopFileId || !ipeProofFileId) {
      setIpeError('Please select both the population file and the IPE proof file.');
      return;
    }
    setIpeLoading(true);
    setIpeError(null);
    try {
      await axiosInstance.post(`/api/control-test-runs/${runId}/population/ipe-verify`, {
        population_file_id: parseInt(ipePopFileId, 10),
        ipe_proof_file_id:  parseInt(ipeProofFileId, 10),
        source_note: ipeSourceNoteInput.trim(),
      });
      setIpeModal(false);
      onReload?.();
    } catch (e) {
      setIpeError(resolve403ErrorMessage(e, e.response?.data?.error || e.message || 'Verification failed'));
    } finally {
      setIpeLoading(false);
    }
  };

  const saveExplanation = async () => {
    if (!runId) return;
    if (!checkTestExecutionsAccess()) return;
    setExplanationSaving(true);
    try {
      await axiosInstance.patch(`/api/control-test-runs/${runId}/population/ipe`, {
        filtered_total_amount:    filteredTotal    ?? 0,
        independent_total_amount: independentTotal ?? 0,
        note: explanationDraft.trim(),
      });
      onReload?.();
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error(
          'Failed to save IPE explanation: ' +
            resolve403ErrorMessage(e, e.response?.data?.error || e.message)
        );
      }
    } finally {
      setExplanationSaving(false);
    }
  };

  const patchPopulation = async (fields) => {
    await axiosInstance.patch(`/api/control-test-runs/${runId}/population`, fields);
    onReload?.();
  };

  const saveNotes = async (val) => {
    await axiosInstance.patch(`/api/control-test-runs/${runId}/population/notes`, {
      notes: val?.trim() || null,
    });
    onReload?.();
  };

  const saveHumanIPEConclusion = async (val) => {
    await axiosInstance.patch(`/api/control-test-runs/${runId}/population/human-ipe-conclusion`, {
      human_ipe_conclusion: val || null,
    });
    onReload?.();
  };

  // Population items exclusion state
  const [itemExcluding, setItemExcluding] = useState(null); // item ID being toggled
  const [excludeFormItem, setExcludeFormItem] = useState(null); // item awaiting reason input
  const [excludeReasonDraft, setExcludeReasonDraft] = useState('');

  const handleToggleExclude = (item) => {
    if (!runId || isLocked) return;
    if (!checkTestExecutionsAccess()) return;
    if (!item.excluded) {
      // Excluding: open the atomic reason form instead of toggling directly.
      setExcludeFormItem(item);
      setExcludeReasonDraft('');
    } else {
      // Un-excluding: no reason required — fire immediately.
      doExclude(item.id, false, null);
    }
  };

  const doExclude = async (itemId, excluded, reason) => {
    setItemExcluding(itemId);
    try {
      await axiosInstance.patch(
        `/api/control-test-runs/${runId}/population/items/${itemId}/exclude`,
        { excluded, exclusion_reason: reason || null }
      );
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to update exclusion.');
    } finally {
      setItemExcluding(null);
    }
  };
  const handleExclusionReasonSave = async (item, reason) => {
    if (!runId || isLocked) return;
    if (!checkTestExecutionsAccess()) return;
    await doExclude(item.id, item.excluded, reason.trim());
  };

  // Auto-generated conclusion paragraph (the reviewer's own text, when saved, is shown instead)
  const conclusionText = ipeConclusionText(facts, period, currency);

  // Prefer saved sample selections on the run; fall back to evidence map
  const { refs: sampleRefs, fieldData, sampleToFile, key: normRef } = populationSamples(run, evidenceMap, attributes);

  const effectiveFields = planFields.length > 0 ? planFields : [];

  return (
    <WPSection numeral="II" title="Population & Sample Selection" noPad>
      {/* IPE verify modal */}
      {ipeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-[500px] p-6 space-y-4">
            <h3 className="text-sm font-bold text-gray-900">Verify Population Completeness (IPE)</h3>
            <p className="text-xs text-gray-600 leading-relaxed">
              Select the population file and an independent corroborating source (e.g. GL summary, system-generated count).
              The AI will compare record counts and totals and record the result.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Population File <span className="text-red-500">*</span></label>
                <select value={ipePopFileId} onChange={e => setIpePopFileId(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400">
                  <option value="">— select file —</option>
                  {ipeFiles.map(f => { const fid = f.file_id ?? f.id; return <option key={fid} value={fid}>{f.file_name || f.original_filename || `File ${fid}`}</option>; })}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">IPE Proof File (Independent Source) <span className="text-red-500">*</span></label>
                <select value={ipeProofFileId} onChange={e => setIpeProofFileId(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400">
                  <option value="">— select file —</option>
                  {ipeFiles.map(f => { const fid = f.file_id ?? f.id; return <option key={`proof-${fid}`} value={fid}>{f.file_name || f.original_filename || `File ${fid}`}</option>; })}
                </select>
              </div>
              {ipeFiles.length === 0 && (
                <p className="text-xs text-amber-600">No evidence files uploaded for this run. Upload files first via the Evidence action.</p>
              )}
              {ipePopFileId && ipeProofFileId && ipePopFileId === ipeProofFileId && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-300 rounded-lg px-3 py-2.5">
                  <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700 leading-relaxed">
                    <span className="font-semibold">Independence violation:</span> The proof file must be a different, independent source — not the same file as the population.
                  </p>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Source Note <span className="text-gray-400 font-normal">(optional)</span></label>
                <input type="text" value={ipeSourceNoteInput} onChange={e => setIpeSourceNoteInput(e.target.value)}
                  placeholder="e.g. GL summary pulled directly from ERP on 2026-04-05"
                  className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400" />
              </div>
            </div>
            {ipeError && <p className="text-xs text-red-600">{ipeError}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={() => setIpeModal(false)} className="text-xs text-gray-500 px-3 py-1.5 rounded hover:bg-gray-100">Cancel</button>
              <button onClick={handleIpeVerify}
                disabled={ipeLoading || !ipePopFileId || !ipeProofFileId || ipePopFileId === ipeProofFileId}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-indigo-600 px-4 py-1.5 rounded hover:bg-indigo-700 disabled:opacity-50">
                {ipeLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                {ipeLoading ? 'Verifying…' : 'Confirm Verify'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Meta bar */}
      <div className="px-5 py-2.5 bg-gray-50 border-b border-gray-200 flex flex-wrap gap-x-8 gap-y-1.5 items-start">
        {populationNotRequired ? (
          <>
            <MetaInline label="Population" val="Not required" />
            <MetaInline label="Period Start" val={run?.testing_period_start ? fmt(run.testing_period_start) : 'Not set'} />
            <MetaInline label="Period End" val={run?.testing_period_end ? fmt(run.testing_period_end) : 'Not set'} />
          </>
        ) : (<>
        <div className="flex items-baseline gap-1.5">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold shrink-0">Total Population:</span>
          <EditableField
            aiValue={totalPop != null ? String(totalPop) : null}
            humanValue={population.human_total_count != null ? String(population.human_total_count) : null}
            fieldType="number"
            showBadge={true}
            emptyLabel="Not specified"
            renderValue={(v) => `${Number(v).toLocaleString()} items`}
            onSave={async (val) => { await patchPopulation({ total_count: val == null ? null : Number(val) }); }}
            onReset={async () => { await patchPopulation({ reset_human_fields: ['total_count'] }); }}
            locked={isLocked}
          />
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold shrink-0">Period Start:</span>
          <EditableField
            aiValue={population.testing_period_start ? fmt(population.testing_period_start) : null}
            humanValue={population.human_testing_period_start ? fmt(population.human_testing_period_start) : null}
            fieldType="date"
            showBadge={true}
            emptyLabel="Not set"
            onSave={async (val) => { await patchPopulation({ testing_period_start: val || null }); }}
            onReset={async () => { await patchPopulation({ reset_human_fields: ['testing_period_start'] }); }}
            locked={isLocked}
          />
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold shrink-0">Period End:</span>
          <EditableField
            aiValue={population.testing_period_end ? fmt(population.testing_period_end) : null}
            humanValue={population.human_testing_period_end ? fmt(population.human_testing_period_end) : null}
            fieldType="date"
            showBadge={true}
            emptyLabel="Not set"
            onSave={async (val) => { await patchPopulation({ testing_period_end: val || null }); }}
            onReset={async () => { await patchPopulation({ reset_human_fields: ['testing_period_end'] }); }}
            locked={isLocked}
          />
        </div>
        </>)}
        <MetaInline label="Sample Size" val={`${sampleRefs.length} item${sampleRefs.length !== 1 ? 's' : ''}`} />
        <MetaInline label="Selection Method" val={selectionText} />
        {!populationNotRequired && identifierColumn && <MetaInline label="Identifier Column" val={identifierColumn} />}
        {!populationNotRequired && (
        <div className="flex items-center gap-2">
          <MetaInline label="Population Completeness"
            val={isVerified ? '✓ Verified' : isException ? '⚠ Exception' : 'Pending verification'} />
          <button onClick={openIpeModal}
            className="text-[10px] font-semibold text-indigo-600 border border-indigo-300 bg-indigo-50 px-2 py-0.5 rounded hover:bg-indigo-100">
            {(hasIPEData || legacyIPEValidated) ? 'Re-verify IPE' : 'Verify IPE'}
          </button>
        </div>
        )}
      </div>

      {populationNotRequired && (
        <PopulationNotRequiredPanel
          run={run}
          runId={runId}
          isLocked={isLocked}
          onReload={onReload}
          sampleCount={sampleRefs.length}
          rationale={populationRationale}
        />
      )}

      {!populationNotRequired && (
      <>
      {/* ── Completeness & Accuracy Verification ──────────────────────────────── */}
      <div className="px-5 py-3 border-b border-gray-200 space-y-2">
        {/* Section header with "C" pill badge */}
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-md text-white text-[11px] font-bold shrink-0 ${hasIPEData ? 'bg-blue-600' : 'bg-gray-300'}`}>C</span>
          <span className={`text-sm font-semibold ${hasIPEData ? 'text-gray-800' : 'text-gray-400'}`}>
            Completeness &amp; Accuracy Verification
          </span>
          {hasIPEData && (
            <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${isVerified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {isVerified ? 'Tied Out' : 'Exception'}
            </span>
          )}
        </div>

        {/* Not yet verified */}
        {!hasIPEData && !legacyIPEValidated && (
          <p className="text-xs text-gray-400 pl-7">
            Population completeness has not been verified. Click <strong>Verify IPE</strong> above to agree the population extract to an independent source.
          </p>
        )}

        {/* Legacy verified — no structured data yet */}
        {!hasIPEData && legacyIPEValidated && (
          <div className="pl-7 flex items-center gap-2 text-xs text-green-800">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-600" />
            Population completeness was verified during test setup. Click <strong>Re-verify IPE</strong> above to generate a detailed workpaper comparison.
          </div>
        )}

        {/* Main C&A result card */}
        {hasIPEData && (
          <div className={`border rounded-xl p-3 space-y-3 ${isVerified ? 'border-green-200 bg-green-50/40' : 'border-red-200 bg-red-50/40'}`}>

            {/* Header row */}
            <div className="flex items-start gap-3">
              {isVerified
                ? <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                : <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />}
              <div>
                <p className={`text-sm font-bold ${isVerified ? 'text-green-900' : 'text-red-900'}`}>
                  {isVerified ? 'IPE Tied Out — Population Verified' : 'IPE Exception — Discrepancy Exceeds Tolerance'}
                </p>
                {ipeVerifiedAt && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Documented {new Date(ipeVerifiedAt).toLocaleString()}
                  </p>
                )}
              </div>
            </div>

            {/* Metric table: METRIC / POPULATION / IPE PROOF / DIFF / TOLERANCE / RESULT */}
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white text-xs">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Metric</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-500 uppercase tracking-wide">Population</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-500 uppercase tracking-wide">IPE Proof</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-500 uppercase tracking-wide">Diff</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-500 uppercase tracking-wide">Tolerance</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500 uppercase tracking-wide">Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {/* Rows: features/control-testing/report/population.ts (tieOutRows), shared with the working paper */}
                  {tieOutRows(run, facts, period, currency).map((row) => (
                    <tr key={row.id} data-testid={row.id === 'period' ? 'period-row' : undefined}>
                      <td className="px-3 py-2 font-medium text-gray-700">
                        {row.metric}
                        {row.note && (
                          <span className="block text-[10px] font-normal text-gray-400">{row.note}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-800 font-medium">{row.population}</td>
                      <td className="px-3 py-2 text-right text-gray-800 font-medium">{row.proof}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{row.diff}</td>
                      <td className="px-3 py-2 text-right text-gray-500">{row.tolerance}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={row.result === 'match' || row.result === 'within' ? 'text-green-600 font-semibold'
                          : row.result === 'none' ? 'text-gray-400' : 'text-red-600 font-semibold'}>{row.resultText}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* File metadata */}
            <div className="text-xs space-y-0.5 text-gray-600 border-t border-gray-100 pt-2">
              {popFileName && (
                <p>
                  <span className="font-medium">Population file:</span>{' '}
                  {popFileName}
                </p>
              )}
              {proofFileName && (
                <p>
                  <span className="font-medium">IPE proof file:</span>{' '}
                  {proofFileName}
                </p>
              )}
              <div className="flex items-start gap-1.5">
                <span className="font-medium shrink-0">Source system:</span>
                <EditableField
                  aiValue={ipeSourceNote}
                  humanValue={population.human_ipe_source_note || null}
                  fieldType="text"
                  showBadge={true}
                  emptyLabel="Not specified"
                  onSave={async (val) => { await patchPopulation({ ipe_source_note: val || null }); }}
                  onReset={async () => { await patchPopulation({ ipe_source_note: '' }); }}
                  locked={isLocked}
                />
              </div>
            </div>

            {/* Auto-generated conclusion paragraph — editable with AI suggestion */}
            {(conclusionText || population.human_ipe_conclusion) && (
              <div className={`rounded-lg border px-4 py-3 ${isVerified ? 'bg-green-50 border-green-200 text-green-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
                <p className="font-semibold text-[10px] uppercase tracking-wide mb-2">{isVerified ? 'Conclusion:' : 'Exception:'}</p>
                <EditableField
                  aiValue={conclusionText || null}
                  humanValue={population.human_ipe_conclusion || null}
                  fieldType="textarea"
                  showBadge={true}
                  placeholder="Document the C&A conclusion…"
                  emptyLabel="No conclusion recorded."
                  onSave={saveHumanIPEConclusion}
                  onReset={() => saveHumanIPEConclusion(null)}
                  locked={isLocked}
                />
              </div>
            )}

            {/* Variance explanation — required for exceptions before sign-off */}
            {isException && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-700">
                    Auditor's Explanation for Variance <span className="text-red-500">*</span>
                  </label>
                  <button onClick={saveExplanation} disabled={explanationSaving || !explanationDraft.trim()}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-amber-600 px-3 py-1 rounded hover:bg-amber-700 disabled:opacity-50 shrink-0">
                    {explanationSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                    {explanationSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
                <textarea
                  value={explanationDraft}
                  onChange={e => setExplanationDraft(e.target.value)}
                  rows={2}
                  placeholder="Document why the variance is acceptable (e.g. timing difference — 2 invoices crossed the quarter boundary, confirmed as Q2)."
                  className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                />
              </div>
            )}
          </div>
        )}
      </div>
      </>
      )}

      {!populationNotRequired && (
      <>
      {/* Population notes */}
      <div className="px-5 py-4 border-t border-gray-100">
        <div className="flex items-center gap-1.5 mb-2">
          <PenLine className="h-3.5 w-3.5 text-gray-400" />
          <span className="text-xs font-semibold text-gray-700">Auditor Population Notes</span>
        </div>
        <EditableField
          aiValue={null}
          humanValue={population.notes || null}
          fieldType="textarea"
          placeholder="Document any population scope decisions, exclusions rationale, or completeness observations…"
          emptyLabel="No notes recorded."
          showBadge={false}
          onSave={saveNotes}
          onReset={() => saveNotes(null)}
          locked={isLocked}
        />
      </div>
      </>
      )}

      {/* Population items exclusion management */}
      {Array.isArray(population.items) && population.items.length > 0 && (
        <PopulationItemsExclusionSection
          items={population.items}
          runId={runId}
          isLocked={isLocked}
          itemExcluding={itemExcluding}
          onToggleExclude={handleToggleExclude}
          onReasonSave={handleExclusionReasonSave}
          excludeFormItem={excludeFormItem}
          excludeReasonDraft={excludeReasonDraft}
          onExcludeReasonChange={setExcludeReasonDraft}
          onExcludeFormConfirm={() => {
            if (!excludeReasonDraft.trim()) { toast.error('Exclusion reason is required.'); return; }
            doExclude(excludeFormItem.id, true, excludeReasonDraft.trim());
            setExcludeFormItem(null);
          }}
          onExcludeFormCancel={() => setExcludeFormItem(null)}
        />
      )}

      {/* Sample table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b-2 border-gray-300 bg-gray-100">
              <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wide w-10">Item</th>
              <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wide">Sample Ref</th>
              {effectiveFields.map(f => (
                <th key={f.id} className="px-3 py-2 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wide">{f.label}</th>
              ))}
              <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wide">Evidence File</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sampleRefs.map((ref, i) => (
              <tr key={ref} className="hover:bg-gray-50">
                <td className="px-3 py-2 text-xs text-gray-500 font-mono">{i + 1}</td>
                <td className="px-3 py-2 text-xs font-mono text-gray-800 font-semibold">{ref}</td>
                {effectiveFields.map(f => (
                  <td key={f.id} className="px-3 py-2 text-xs text-gray-700">
                    {fieldData[normRef(ref)]?.[f.label] || fieldData[normRef(ref)]?.[f.field_key] || '—'}
                  </td>
                ))}
                <td className="px-3 py-2 text-xs text-gray-500">
                  {sampleToFile[normRef(ref)]
                    ? <span className="flex items-center gap-1"><FileText className="h-3 w-3 text-gray-400 shrink-0" />{sampleToFile[normRef(ref)]}</span>
                    : '—'}
                </td>
              </tr>
            ))}
            {sampleRefs.length === 0 && (
              <tr><td colSpan={3 + effectiveFields.length} className="px-3 py-6 text-xs text-gray-400 text-center italic">No samples recorded for this run.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="px-5 py-2 bg-gray-50 border-t border-gray-100 text-[10px] text-gray-500">
        {populationNotRequired
          ? <>^ Samples are groups of evidence files chosen by the auditor (no population). Evidence files correspond to Exhibits E-1 through E-{sampleRefs.length}.</>
          : <>^ Items selected via {selectionLabel.toLowerCase()} from the population. Evidence files correspond to Exhibits E-1 through E-{sampleRefs.length}.</>}
      </div>
    </WPSection>
  );
}

/* ── Population items exclusion manager ─────────────────────────────────────── */
function PopulationItemsExclusionSection({ items, runId, isLocked, itemExcluding, onToggleExclude, onReasonSave,
  excludeFormItem, excludeReasonDraft, onExcludeReasonChange, onExcludeFormConfirm, onExcludeFormCancel }) {
  const [showAll, setShowAll]           = useState(false);
  const [editReasonId, setEditReasonId] = useState(null);
  const [reasonDraft, setReasonDraft]   = useState('');

  const excludedItems = items.filter(it => it.excluded);
  const displayItems  = showAll ? items : excludedItems;

  if (items.length === 0) return null;

  return (
    <div className="border-t border-gray-100 px-5 py-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
          <Shield className="h-3.5 w-3.5 text-gray-400" />
          Population Items
          <span className="ml-1 font-normal text-gray-400">({items.length} total · {excludedItems.length} excluded)</span>
        </h4>
        <button
          onClick={() => setShowAll(v => !v)}
          className="text-[10px] text-indigo-500 hover:text-indigo-700 flex items-center gap-0.5"
        >
          {showAll ? 'Show excluded only' : `Show all ${items.length} items`}
          <ChevronDown className={`h-3 w-3 transition-transform ${showAll ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Atomic exclusion form — shown when user clicks Exclude on a not-yet-excluded item */}
      {excludeFormItem && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-xs font-semibold text-red-700 mb-1.5">
            Excluding <span className="font-mono">{excludeFormItem.record_ref}</span> — reason required
          </p>
          <div className="flex items-center gap-2">
            <input
              autoFocus
              value={excludeReasonDraft}
              onChange={e => onExcludeReasonChange(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') onExcludeFormConfirm(); if (e.key === 'Escape') onExcludeFormCancel(); }}
              placeholder="Why is this item excluded?"
              className="flex-1 text-xs border border-red-300 rounded px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-red-400 bg-white"
            />
            <button onClick={onExcludeFormConfirm}
              className="text-xs font-semibold text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded">Confirm</button>
            <button onClick={onExcludeFormCancel}
              className="text-xs text-gray-500 border border-gray-200 px-3 py-1.5 rounded hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      {displayItems.length === 0 ? (
        <p className="text-[10px] text-gray-400 italic">No items excluded. {!isLocked && 'Click "Show all items" to review and exclude items.'}</p>
      ) : (
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Record Ref</th>
                <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Exclusion Reason</th>
                <th className="px-3 py-2 text-center text-[10px] font-bold text-gray-500 uppercase w-24">Excluded</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayItems.map(item => (
                <tr key={item.id} className={item.excluded ? 'bg-red-50/40' : 'hover:bg-gray-50'}>
                  <td className="px-3 py-2 font-mono text-gray-800">{item.record_ref}</td>
                  <td className="px-3 py-2">
                    {editReasonId === item.id && !isLocked ? (
                      <div className="flex items-center gap-1">
                        <input
                          autoFocus
                          value={reasonDraft}
                          onChange={e => setReasonDraft(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { onReasonSave(item, reasonDraft); setEditReasonId(null); }
                            if (e.key === 'Escape') setEditReasonId(null);
                          }}
                          className="flex-1 border border-indigo-300 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400"
                          placeholder="Why is this item excluded?"
                        />
                        <button onClick={() => { onReasonSave(item, reasonDraft); setEditReasonId(null); }}
                          className="text-[10px] text-indigo-600 hover:text-indigo-800 font-semibold">Save</button>
                        <button onClick={() => setEditReasonId(null)}
                          className="text-[10px] text-gray-400 hover:text-gray-600">Cancel</button>
                      </div>
                    ) : (
                      <span
                        onClick={() => { if (!isLocked && item.excluded) { setReasonDraft(item.exclusion_reason || ''); setEditReasonId(item.id); } }}
                        className={`${!isLocked && item.excluded ? 'cursor-pointer hover:text-indigo-600 group' : ''} text-gray-600`}
                      >
                        {item.exclusion_reason || <span className="text-gray-300 italic">No reason{!isLocked && item.excluded ? ' — click to add' : ''}</span>}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {isLocked ? (
                      item.excluded
                        ? <span className="text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded">Excluded</span>
                        : <span className="text-[10px] text-gray-400">—</span>
                    ) : (
                      <button
                        onClick={() => onToggleExclude(item)}
                        disabled={itemExcluding === item.id}
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded border transition-colors ${
                          item.excluded
                            ? 'bg-red-50 border-red-300 text-red-700 hover:bg-white hover:text-gray-600'
                            : 'bg-white border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-600'
                        } disabled:opacity-50`}
                      >
                        {itemExcluding === item.id
                          ? '…'
                          : item.excluded ? 'Excluded ✕' : 'Include'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── Zoomable image (inline evidence viewer) ────────────────────────────────── */
const INLINE_ZOOM_MIN = 0.25;
const INLINE_ZOOM_MAX = 8;

function ZoomableImage({ src, alt }) {
  const [view, _setView] = useState({ zoom: 1, panX: 0, panY: 0 });
  const viewRef = useRef(view);
  const setView = useCallback((updater) => {
    _setView(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      viewRef.current = next;
      return next;
    });
  }, []);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ active: false, startX: 0, startY: 0, initPanX: 0, initPanY: 0 });
  const containerRef = useRef(null);

  const resetView = useCallback(() => setView({ zoom: 1, panX: 0, panY: 0 }), [setView]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.12 : 0.88;
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;
      setView(v => {
        const nextZoom = Math.min(Math.max(v.zoom * factor, INLINE_ZOOM_MIN), INLINE_ZOOM_MAX);
        return { zoom: nextZoom, panX: cx - ((cx - v.panX) * nextZoom / v.zoom), panY: cy - ((cy - v.panY) * nextZoom / v.zoom) };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [setView]);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.active) return;
      setView(v => ({ ...v, panX: dragRef.current.initPanX + (e.clientX - dragRef.current.startX), panY: dragRef.current.initPanY + (e.clientY - dragRef.current.startY) }));
    };
    const onUp = () => { if (!dragRef.current.active) return; dragRef.current.active = false; setIsDragging(false); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [setView]);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragRef.current = { active: true, startX: e.clientX, startY: e.clientY, initPanX: viewRef.current.panX, initPanY: viewRef.current.panY };
    setIsDragging(true);
  }, []);

  const zoomPct = Math.round(view.zoom * 100);

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-0.5 px-2 py-1 bg-gray-100 border-b border-gray-200">
        <button onClick={() => setView(v => ({ ...v, zoom: Math.max(v.zoom / 1.25, INLINE_ZOOM_MIN) }))} disabled={view.zoom <= INLINE_ZOOM_MIN}
          className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30 rounded transition-colors" title="Zoom out">
          <Minus className="h-3 w-3" />
        </button>
        <button onClick={resetView} className="px-1.5 py-0.5 text-xs font-mono text-gray-600 hover:text-gray-900 min-w-[3rem] text-center rounded hover:bg-gray-200 transition-colors" title="Reset zoom">
          {zoomPct}%
        </button>
        <button onClick={() => setView(v => ({ ...v, zoom: Math.min(v.zoom * 1.25, INLINE_ZOOM_MAX) }))} disabled={view.zoom >= INLINE_ZOOM_MAX}
          className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30 rounded transition-colors" title="Zoom in">
          <Plus className="h-3 w-3" />
        </button>
        <span className="text-xs text-gray-400 ml-2">Scroll · Drag · Double-click to reset</span>
      </div>
      <div ref={containerRef} className="relative overflow-hidden select-none bg-gray-50"
        style={{ height: '500px', cursor: isDragging ? 'grabbing' : view.zoom > 1 ? 'grab' : 'default' }}
        onMouseDown={handleMouseDown}>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          transform: `translate(${view.panX}px, ${view.panY}px) scale(${view.zoom})`,
          transformOrigin: 'center center', transition: isDragging ? 'none' : 'transform 0.12s ease-out' }}
          onDoubleClick={resetView}>
          <img src={src} alt={alt} draggable={false}
            className="object-contain rounded border border-gray-200"
            style={{ maxWidth: 'calc(100% - 16px)', maxHeight: 'calc(100% - 16px)' }} />
        </div>
      </div>
    </div>
  );
}

/* ── Evidence Annotations (per attribute) ───────────────────────────────────── */
function AttributeEvidenceAnnotations({ attr, sampleToLocator }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [annotations, setAnnotations] = useState({});
  const [viewMode, setViewMode] = useState({});
  const fetchedRef = useRef(new Set());

  const uniqueEvidence = React.useMemo(() => {
    const seen = new Map();
    for (const sr of (attr.sample_results || [])) {
      const cite = sr.citation;
      if (!cite) continue;
      const ref = norm(sr.record_ref);
      const locator = sampleToLocator[ref] || {};
      const fileId = cite.source_file_id || locator.source_file_id;
      const fileName = cite.source_file || locator.source_file || '';
      // F3 fix: capture file_ref (GCS path) from locator so the AI service
      // can fetch the file even when the legacy file_id backend path fails.
      const fileRef = cite.file_ref || locator.file_ref || null;
      if (!fileId) continue;
      const key = `${fileId}|${cite.sheet_name || ''}|${cite.page_number || ''}`;
      if (!seen.has(key)) {
        seen.set(key, {
          source_file: fileName,
          source_file_id: fileId,
          file_ref: fileRef,
          sheet_name: cite.sheet_name || null,
          page_number: cite.page_number || null,
          row_numbers: cite.row_numbers || null,
          cell_ref: cite.cell_ref || null,
          excerpt: cite.excerpt || '',
        });
      }
    }
    return Array.from(seen.entries()).map(([key, citation]) => ({ key, citation }));
  }, [attr, sampleToLocator]);

  useEffect(() => {
    if (!isExpanded || uniqueEvidence.length === 0) return;
    for (const { key, citation } of uniqueEvidence) {
      if (fetchedRef.current.has(key)) continue;
      fetchedRef.current.add(key);
      setAnnotations(prev => ({ ...prev, [key]: { annotatedUrl: null, originalUrl: null, loading: true, error: null } }));
      setViewMode(prev => ({ ...prev, [key]: 'annotated' }));
      const requestBody = {
        file_id: citation.source_file_id,
        // F3 fix: pass file_ref so the AI service can fall back to GCS fetch
        // when the legacy file_id backend lookup returns nothing (e.g. 204).
        file_ref: citation.file_ref || null,
        filename: citation.source_file || '',
        citation: {
          source_file: citation.source_file || '',
          source_file_id: citation.source_file_id,
          sheet_name: citation.sheet_name || null,
          page_number: citation.page_number || null,
          row_numbers: citation.row_numbers || null,
          cell_ref: citation.cell_ref || null,
          excerpt: citation.excerpt || '',
        },
        findings_text: attr.findings_summary || '',
      };
      const fetchImage = async (url, timeout) => {
        try {
          const resp = await axiosInstance.post(url, requestBody, { responseType: 'arraybuffer', timeout });
          if (resp.status === 200 && resp.data?.byteLength > 0) {
            return URL.createObjectURL(new Blob([resp.data], { type: 'image/png' }));
          }
          return null;
        } catch { return null; }
      };
      (async () => {
        const annotatedUrl = await fetchImage('/api/evidence/annotate', 90000);
        const originalUrl  = await fetchImage('/api/evidence/screenshot', 60000);
        setAnnotations(prev => ({
          ...prev,
          [key]: { annotatedUrl, originalUrl, loading: false, error: !annotatedUrl && !originalUrl ? 'No evidence available' : null },
        }));
      })();
    }
  }, [isExpanded, uniqueEvidence, attr]);

  useEffect(() => {
    return () => {
      Object.values(annotations).forEach(a => {
        if (a.annotatedUrl) URL.revokeObjectURL(a.annotatedUrl);
        if (a.originalUrl)  URL.revokeObjectURL(a.originalUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (uniqueEvidence.length === 0) return null;

  return (
    <div className="mx-5 mb-3 border border-purple-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(p => !p)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-purple-50 hover:bg-purple-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-purple-600" />
          <span className="text-xs font-medium text-purple-900">Evidence Annotations ({uniqueEvidence.length})</span>
        </div>
        {isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-purple-600" /> : <ChevronDown className="h-3.5 w-3.5 text-purple-600" />}
      </button>
      {isExpanded && (
        <div className="p-3 space-y-3 bg-white">
          {uniqueEvidence.map(({ key, citation }) => {
            const ann = annotations[key];
            return (
              <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-2 min-w-0">
                  <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-900 truncate">{citation.source_file || 'Evidence'}</span>
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    {citation.sheet_name && `Sheet: ${citation.sheet_name}`}
                    {citation.page_number && ` · Page ${citation.page_number}`}
                  </span>
                </div>
                {ann && !ann.loading && (ann.annotatedUrl || ann.originalUrl) && (
                  <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    {['annotated', 'original'].map(mode => (
                      <button key={mode}
                        onClick={() => setViewMode(prev => ({ ...prev, [key]: mode }))}
                        className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${(viewMode[key] || 'annotated') === mode
                          ? (mode === 'annotated' ? 'bg-purple-600 text-white' : 'bg-blue-600 text-white')
                          : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                      >{mode.charAt(0).toUpperCase() + mode.slice(1)}</button>
                    ))}
                  </div>
                )}
                <div className="bg-gray-50">
                  {ann?.loading && (
                    <div className="flex items-center justify-center gap-2 min-h-[100px]">
                      <Loader2 className="h-5 w-5 text-purple-500 animate-spin" />
                      <span className="text-sm text-gray-500">Loading evidence…</span>
                    </div>
                  )}
                  {ann && !ann.loading && (() => {
                    const mode = viewMode[key] || 'annotated';
                    const url  = mode === 'annotated' ? (ann.annotatedUrl || ann.originalUrl) : (ann.originalUrl || ann.annotatedUrl);
                    return url ? <ZoomableImage src={url} alt={`${mode} evidence`} /> : null;
                  })()}
                  {ann?.error && (
                    <div className="flex items-center justify-center gap-2 min-h-[100px] text-sm text-gray-400">
                      <AlertCircle className="h-4 w-4" />{ann.error}
                    </div>
                  )}
                  {!ann && <div className="flex items-center justify-center min-h-[100px]"><span className="text-sm text-gray-400">Expand to load evidence</span></div>}
                </div>
              </div>
            );
          })}
          <p className="text-xs text-amber-600 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3 flex-shrink-0" />
            AI annotations may alter visual appearance. Always verify against original evidence.
          </p>
        </div>
      )}
    </div>
  );
}

/* ── III. Work Performed ────────────────────────────────────────────────────── */
// F4 — Simplified: show procedure steps only (no per-sample columns/matrix)
const WP_STATUS_OPTS = [
  { value: 'passed',  label: 'Passed' },
  { value: 'failed',  label: 'Exception' },
  { value: 'warning', label: 'Inconclusive' },
];

function WPStatusBadge({ status }) {
  const s = (status || '').toLowerCase();
  if (s === 'passed')  return <span className="text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5">PASS</span>;
  if (s === 'failed')  return <span className="text-[10px] font-bold text-red-700 bg-red-50 border border-red-300 px-1.5 py-0.5">EXCEPTION</span>;
  if (s === 'warning') return <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">INCONCLUSIVE</span>;
  return <span className="text-[10px] text-gray-400">—</span>;
}

function WPFieldRow({ label, children }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide w-28 shrink-0 pt-0.5">{label}</span>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

function WorkPerformedSection({ attributes, planAttributes, numeral = 'III', runId, isLocked, onReload }) {
  const [expandedAttrs, setExpandedAttrs] = useState(new Set(attributes.map(a => a.attribute_ref)));
  // keyed by attr.id → null (closed) | '' | string (open, editing reason)
  const [rerunDrafts, setRerunDrafts] = useState({});

  const toggle = (ref) => setExpandedAttrs(prev => {
    const s = new Set(prev);
    s.has(ref) ? s.delete(ref) : s.add(ref);
    return s;
  });

  const planStepsByRef = {};
  planAttributes.forEach(pa => {
    const key = pa.display_ref || pa.id;
    if (key) planStepsByRef[key] = pa.steps || [];
  });

  const patchAttr = async (attrResultId, payload) => {
    await axiosInstance.patch(
      `/api/control-test-runs/${runId}/attribute-results/${attrResultId}/human-override`,
      payload
    );
    onReload?.();
  };

  const patchNeedsRerun = async (attrResultId, flag, reason) => {
    try {
      await axiosInstance.patch(
        `/api/control-test-runs/${runId}/attribute-results/${attrResultId}/needs-rerun`,
        { needs_rerun: flag, reason: reason || undefined }
      );
      onReload?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update re-run flag.');
    }
  };

  return (
    <WPSection numeral={numeral} title="Work Performed" noPad>
      <div className="divide-y divide-gray-200">
        {attributes.map((attr) => {
          const ref      = attr.attribute_ref || '?';
          const expanded = expandedAttrs.has(ref);
          const planSteps = planStepsByRef[ref] || [];
          const steps = planSteps.length > 0 ? planSteps
            : Array.isArray(attr.steps_snapshot) ? attr.steps_snapshot
            : [];

          const effectiveStatus = attr.human_status ?? attr.ai_status ?? attr.status;

          // Build AI steps as a single combined text block
          const aiStepsText = steps.length > 0
            ? steps.map((s, i) => `${s.step_number ?? i + 1}. ${s.instruction || s.description || s.audit_procedure || ''}`.trim()).join('\n\n')
            : null;

          return (
            <div key={ref}>
              <div className="flex items-center gap-1 px-5 py-3 hover:bg-gray-50">
                <button
                  type="button"
                  onClick={() => toggle(ref)}
                  className="flex-1 flex items-center gap-3 text-left min-w-0"
                >
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 border border-slate-300 text-slate-800 text-sm font-bold shrink-0">{ref}</span>
                  <span className="flex-1 min-w-0">
                    <span className="text-sm font-semibold text-gray-900">{attr.label || '—'}</span>
                    {attr.description && <span className="block text-xs text-gray-500 truncate mt-0.5">{attr.description}</span>}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ml-2 ${expanded ? 'rotate-180' : ''}`} />
                </button>
                {/* Status — editable, separate from expand toggle */}
                <div className="shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                  {runId && attr.id ? (
                    <EditableField
                      aiValue={attr.ai_status ?? attr.status ?? null}
                      humanValue={attr.human_status || null}
                      fieldType="select"
                      options={WP_STATUS_OPTS}
                      renderValue={(v) => <WPStatusBadge status={v} />}
                      onSave={(val) => patchAttr(attr.id, { human_status: val || null })}
                      onReset={() => patchAttr(attr.id, { human_status: '' })}
                      locked={isLocked}
                      showBadge={true}
                      tooltipAlign="right"
                    />
                  ) : (
                    <WPStatusBadge status={effectiveStatus} />
                  )}
                </div>
              </div>

              {expanded && (
                <div className="border-t border-gray-100 px-5 py-3 space-y-3">
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-2">Procedure Steps</p>
                    {runId && attr.id ? (
                      <EditableField
                        aiValue={aiStepsText}
                        humanValue={attr.human_steps_text || null}
                        fieldType="textarea"
                        placeholder="Describe the audit procedure steps performed…"
                        emptyLabel="No steps recorded."
                        onSave={(val) => patchAttr(attr.id, { human_steps_text: val || null })}
                        onReset={() => patchAttr(attr.id, { human_steps_text: '' })}
                        locked={isLocked}
                      />
                    ) : (
                      <p className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed">
                        {aiStepsText || <span className="text-gray-400 italic">No steps recorded.</span>}
                      </p>
                    )}
                  </div>

                  {/* Re-run flag — hidden for now (SHOW_RERUN_FLAG); only shown when editable and the run has been executed */}
                  {SHOW_RERUN_FLAG && runId && attr.id && !isLocked && (() => {
                    const draft = rerunDrafts[attr.id];
                    if (attr.needs_rerun && draft == null) {
                      return (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-300 px-1.5 py-0.5 rounded">
                            <Flag className="h-2.5 w-2.5" /> Flagged for re-run
                          </span>
                          {attr.needs_rerun_reason && (
                            <span className="text-[10px] text-gray-500 italic max-w-[220px] truncate">{attr.needs_rerun_reason}</span>
                          )}
                          <button
                            onClick={() => patchNeedsRerun(attr.id, false, null)}
                            className="text-[10px] text-gray-400 hover:text-gray-700 underline underline-offset-2"
                          >Clear flag</button>
                        </div>
                      );
                    }
                    if (draft != null) {
                      return (
                        <div className="flex flex-col gap-1.5">
                          <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wide">Flag for Re-run</p>
                          <textarea
                            value={draft}
                            onChange={(e) => setRerunDrafts(prev => ({ ...prev, [attr.id]: e.target.value }))}
                            placeholder="Reason for re-run (required)…"
                            rows={2}
                            className="w-full text-xs border border-amber-300 rounded px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-amber-400"
                          />
                          <div className="flex items-center gap-2">
                            <button
                              onClick={async () => {
                                await patchNeedsRerun(attr.id, true, draft);
                                setRerunDrafts(prev => { const n = { ...prev }; delete n[attr.id]; return n; });
                              }}
                              disabled={!draft.trim()}
                              className="inline-flex items-center gap-1 text-xs font-medium bg-amber-600 text-white px-2.5 py-1 rounded hover:bg-amber-700 disabled:opacity-50"
                            >
                              <Flag className="h-3 w-3" /> Flag
                            </button>
                            <button
                              onClick={() => setRerunDrafts(prev => { const n = { ...prev }; delete n[attr.id]; return n; })}
                              className="text-xs text-gray-500 border border-gray-300 px-2.5 py-1 rounded hover:bg-gray-50"
                            >Cancel</button>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <button
                        onClick={() => setRerunDrafts(prev => ({ ...prev, [attr.id]: attr.needs_rerun_reason || '' }))}
                        className="inline-flex items-center gap-1 text-[10px] text-amber-600 hover:text-amber-800"
                      >
                        <Flag className="h-2.5 w-2.5" /> Flag for re-run
                      </button>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </WPSection>
  );
}

/* ── IV. Attribute Testing Table ────────────────────────────────────────────── */
// One row per line item (see SampleDataTable). The sample list / review workspace and the one-row-per-sample
// table this replaced are gone: every sample's line items are already in this table.
function AttributeTestingTableSection({ attributes, planFields, livePlanFields, evidenceMap, run, runId, onAfterOverride }) {
  const currency = runCurrency(run);
  // The run's own samples (see resolveExpectedSamples): leftover evidence-map cells are not samples.
  const expectedSamples = resolveExpectedSamples(run, evidenceMap);
  const isLocked = run?.workflow_status === 'signed_off';
  const effectiveRunId = runId || run?.id || null;

  return (
    <WPSection numeral="IV" title="Attribute Testing Table — Operating Effectiveness" noPad>
      <SampleDataTable
        attributes={attributes}
        planFields={planFields}
        livePlanFields={livePlanFields}
        evidenceMap={evidenceMap}
        expectedSamples={expectedSamples}
        runId={effectiveRunId}
        isLocked={isLocked}
        onAfterOverride={onAfterOverride}
        currency={currency}
      />
    </WPSection>
  );
}

/* ── V. Exceptions ──────────────────────────────────────────────────────────── */
const DISPOSITIONS = [
  { value: 'open',          label: 'Open',          cls: 'text-red-700 bg-red-50 border-red-300' },
  { value: 'remediated',    label: 'Remediated',    cls: 'text-green-700 bg-green-50 border-green-300' },
  { value: 'accepted_risk', label: 'Accepted Risk',  cls: 'text-amber-700 bg-amber-50 border-amber-300' },
];

function ExceptionCard({ ex, runId, exNum }) {
  const { checkAccess: checkTestExecutionsAccess } = useTestExecutionAccessGuard();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [form, setForm]       = useState({
    disposition:         ex.disposition || 'open',
    root_cause:          ex.root_cause || '',
    remediation:         ex.remediation || '',
    owner:               ex.owner || '',
    management_response: ex.management_response || '',
  });

  const sevBadge = (s) => {
    const u = (s || '').toUpperCase();
    if (u === 'CRITICAL') return <span className="text-[10px] font-bold text-red-700 bg-red-100 border border-red-300 px-2 py-0.5 uppercase">Critical</span>;
    if (u === 'HIGH')     return <span className="text-[10px] font-bold text-orange-700 bg-orange-100 border border-orange-300 px-2 py-0.5 uppercase">High</span>;
    if (u === 'MEDIUM')   return <span className="text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-300 px-2 py-0.5 uppercase">Medium</span>;
    return                       <span className="text-[10px] font-bold text-gray-600 bg-gray-100 border border-gray-300 px-2 py-0.5 uppercase">{s || 'Low'}</span>;
  };

  const dispInfo = DISPOSITIONS.find(d => d.value === form.disposition) || DISPOSITIONS[0];

  const handleSave = async () => {
    if (!runId || !ex.id) return;
    if (!checkTestExecutionsAccess()) return;
    setSaving(true);
    try {
      await axiosInstance.patch(`/api/control-test-runs/${runId}/exceptions/${ex.id}`, form);
      setEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      toast.error('Save failed: ' + resolve403ErrorMessage(e, e?.response?.data?.error || e?.message || 'unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const field = (key, placeholder, rows = 2) => (
    <textarea
      className="w-full text-xs border border-indigo-300 rounded px-2.5 py-2 text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-400 resize-none"
      rows={rows}
      value={form[key]}
      onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
      placeholder={placeholder}
    />
  );

  return (
    <div className="px-5 py-4">
      {/* Header row */}
      <div className="flex items-center gap-3 flex-wrap mb-3">
        <span className="text-xs font-bold text-gray-500 font-mono border border-gray-300 px-2 py-0.5 bg-gray-50">EX-{String(exNum).padStart(2, '0')}</span>
        {sevBadge(ex.severity)}
        <span className="text-xs font-semibold text-gray-800 font-mono">{ex.record_ref || ex.affected || '—'}</span>
        <span className="text-gray-300">|</span>
        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold border border-slate-300">{ex.attribute_ref}</span>
        <span className="text-xs text-gray-600">{ex.attribute_label}</span>
        {/* Disposition badge */}
        <span className={`text-[10px] font-bold border px-2 py-0.5 rounded-full ${dispInfo.cls}`}>{dispInfo.label}</span>
        {saved && <span className="text-[10px] text-green-600 font-medium">✓ Saved</span>}
        <button
          onClick={() => setEditing(e => !e)}
          className="ml-auto text-[10px] text-indigo-500 hover:text-indigo-700 flex items-center gap-1"
        >
          <PenLine className="h-3 w-3" />{editing ? 'Collapse' : 'Edit'}
        </button>
      </div>

      {/* Memo table */}
      <div className="border border-gray-200 overflow-hidden text-xs">
        {[
          { label: 'Nature of Exception', val: ex.description || ex.issue || ex.finding || '—', hi: true },
          { label: 'Item(s) Affected', val: ex.record_ref || ex.affected || '—' },
          {
            label: 'Auditor Conclusion',
            val: ['CRITICAL','HIGH'].includes((ex.severity || '').toUpperCase())
              ? 'Control exception noted. This represents a deviation from the control objective. No compensating control identified. Recommend escalation to management and remediation in the current period.'
              : 'Control exception noted. The deviation does not appear to represent a systemic failure. Recommend monitoring for recurrence in subsequent periods.',
          },
        ].map(({ label, val, hi }) => (
          <div key={label} className={`flex border-b border-gray-100 ${hi ? 'bg-red-50/30' : ''}`}>
            <div className="w-44 shrink-0 px-3 py-2.5 border-r border-gray-200 bg-gray-50">
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
            </div>
            <div className="px-3 py-2.5 flex-1">
              <span className="text-xs text-gray-700 leading-relaxed">{val}</span>
            </div>
          </div>
        ))}

        {editing ? (
          <div className="border-t border-gray-100">
            {/* Disposition selector */}
            <div className="flex border-b border-gray-100">
              <div className="w-44 shrink-0 px-3 py-2.5 border-r border-gray-200 bg-gray-50">
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Disposition</span>
              </div>
              <div className="px-3 py-2.5 flex-1 flex gap-2 flex-wrap">
                {DISPOSITIONS.map(d => (
                  <button
                    key={d.value}
                    onClick={() => setForm(f => ({ ...f, disposition: d.value }))}
                    className={`text-[10px] font-semibold border px-2.5 py-1 rounded-full ${form.disposition === d.value ? d.cls : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Root cause */}
            <div className="flex border-b border-gray-100">
              <div className="w-44 shrink-0 px-3 py-2.5 border-r border-gray-200 bg-gray-50">
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Root Cause</span>
              </div>
              <div className="px-3 py-2.5 flex-1">{field('root_cause', 'Describe the root cause…')}</div>
            </div>
            {/* Remediation */}
            <div className="flex border-b border-gray-100">
              <div className="w-44 shrink-0 px-3 py-2.5 border-r border-gray-200 bg-gray-50">
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Remediation</span>
              </div>
              <div className="px-3 py-2.5 flex-1">{field('remediation', 'Describe the remediation plan…')}</div>
            </div>
            {/* Owner */}
            <div className="flex border-b border-gray-100">
              <div className="w-44 shrink-0 px-3 py-2.5 border-r border-gray-200 bg-gray-50">
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Owner</span>
              </div>
              <div className="px-3 py-2.5 flex-1">
                <input
                  className="w-full text-xs border border-indigo-300 rounded px-2.5 py-2 text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                  value={form.owner}
                  onChange={e => setForm(f => ({ ...f, owner: e.target.value }))}
                  placeholder="Responsible owner…"
                />
              </div>
            </div>
            {/* Management Response */}
            <div className="flex border-b border-gray-100">
              <div className="w-44 shrink-0 px-3 py-2.5 border-r border-gray-200 bg-gray-50">
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Management Response</span>
              </div>
              <div className="px-3 py-2.5 flex-1">{field('management_response', 'Enter management response…', 3)}</div>
            </div>
            {/* Save / Cancel */}
            <div className="flex justify-end gap-2 px-3 py-2.5 bg-gray-50 border-t border-gray-100">
              <button onClick={() => setEditing(false)} className="text-[10px] text-gray-500 px-2.5 py-1 rounded hover:bg-gray-200">Cancel</button>
              <button
                onClick={handleSave}
                disabled={saving || !runId || !ex.id}
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-white bg-indigo-600 px-3 py-1 rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          /* Read-only rows for saved fields */
          <>
            {(form.root_cause || form.remediation || form.owner || form.management_response) && (
              <>
                {form.root_cause && (
                  <div className="flex border-b border-gray-100">
                    <div className="w-44 shrink-0 px-3 py-2.5 border-r border-gray-200 bg-gray-50"><span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Root Cause</span></div>
                    <div className="px-3 py-2.5 flex-1 text-xs text-gray-700 leading-relaxed">{form.root_cause}</div>
                  </div>
                )}
                {form.remediation && (
                  <div className="flex border-b border-gray-100">
                    <div className="w-44 shrink-0 px-3 py-2.5 border-r border-gray-200 bg-gray-50"><span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Remediation</span></div>
                    <div className="px-3 py-2.5 flex-1 text-xs text-gray-700 leading-relaxed">{form.remediation}</div>
                  </div>
                )}
                {form.owner && (
                  <div className="flex border-b border-gray-100">
                    <div className="w-44 shrink-0 px-3 py-2.5 border-r border-gray-200 bg-gray-50"><span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Owner</span></div>
                    <div className="px-3 py-2.5 flex-1 text-xs text-gray-700">{form.owner}</div>
                  </div>
                )}
                {form.management_response && (
                  <div className="flex">
                    <div className="w-44 shrink-0 px-3 py-2.5 border-r border-gray-200 bg-gray-50"><span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Management Response</span></div>
                    <div className="px-3 py-2.5 flex-1 text-xs text-gray-700 leading-relaxed">{form.management_response}</div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ExceptionMemosSection({ attributes, runId }) {
  const exceptions = [];
  attributes.forEach(attr => {
    (attr.exceptions || []).forEach(ex => {
      exceptions.push({ ...ex, attribute_ref: attr.attribute_ref, attribute_label: attr.label });
    });
  });

  if (exceptions.length === 0) {
    return (
      <WPSection numeral="V" title="Exceptions" badge="None noted">
        <p className="text-xs text-gray-500 italic">
          No exceptions were identified during testing. All attributes were achieved without deficiency for all items selected.
        </p>
      </WPSection>
    );
  }

  return (
    <WPSection numeral="V" title={`Exceptions (${exceptions.length} noted)`} noPad>
      <div className="divide-y divide-gray-200">
        {exceptions.map((ex, i) => (
          <ExceptionCard key={ex.id || i} ex={ex} runId={runId} exNum={i + 1} />
        ))}
      </div>
    </WPSection>
  );
}

/* ── VI. Conclusion ─────────────────────────────────────────────────────────── */
/* ── Verdict Banner (F2) — top-of-page rollup, shown before section details ── */
function VerdictBanner({ run, exec, attributes }) {
  // Derive verdict from stored exec status first; fall back to rolling up live attributes.
  const storedConclusion = (exec?.conclusion || exec?.overall_status || '').toLowerCase();

  let verdict, reason, bannerCls, iconEl;

  if (storedConclusion && storedConclusion !== 'pending') {
    // Use whatever the AI/backend stored
    const isEff    = storedConclusion === 'effective';
    const isIneff  = ['ineffective', 'failed', 'not_effective'].includes(storedConclusion);
    verdict  = isEff ? 'Effective' : isIneff ? 'Not Effective' : 'Inconclusive';
    bannerCls = isEff ? 'bg-green-50 border-green-300 text-green-900'
              : isIneff ? 'bg-red-50 border-red-300 text-red-900'
              : 'bg-amber-50 border-amber-300 text-amber-900';
    iconEl   = isEff ? <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
             : isIneff ? <XCircle className="h-5 w-5 text-red-600 shrink-0" />
             : <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />;
    reason = exec?.executive_summary?.audit_conclusion
      || exec?.executive_summary?.executive_summary
      || null;
  } else if (attributes.length > 0) {
    // Roll up from live attribute statuses
    const statuses = attributes.map(a => (a.overall_status || a.status || '').toLowerCase());
    const anyFailed = statuses.some(s => s === 'failed');
    const allPassed = statuses.every(s => s === 'passed');
    const anyInconclusive = statuses.some(s => ['inconclusive', 'warning'].includes(s));

    if (anyFailed) {
      verdict   = 'Not Effective';
      bannerCls = 'bg-red-50 border-red-300 text-red-900';
      iconEl    = <XCircle className="h-5 w-5 text-red-600 shrink-0" />;
      const failedAttrs = attributes.filter(a => ['failed'].includes((a.overall_status || a.status || '').toLowerCase()));
      reason = `${failedAttrs.map(a => `Attribute ${a.attribute_ref}`).join(', ')} ${failedAttrs.length === 1 ? 'has' : 'have'} exceptions noted.`;
    } else if (allPassed) {
      verdict   = 'Effective';
      bannerCls = 'bg-green-50 border-green-300 text-green-900';
      iconEl    = <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />;
      reason = 'All attributes achieved without exception across tested samples.';
    } else if (anyInconclusive) {
      verdict   = 'Inconclusive';
      bannerCls = 'bg-amber-50 border-amber-300 text-amber-900';
      iconEl    = <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />;
      const incAttrs = attributes.filter(a => ['inconclusive', 'warning'].includes((a.overall_status || a.status || '').toLowerCase()));
      reason = `${incAttrs.map(a => `Attribute ${a.attribute_ref}`).join(', ')} ${incAttrs.length === 1 ? 'requires' : 'require'} additional evidence or manual review.`;
    } else {
      // Still running / no results yet
      return null;
    }
  } else {
    return null;
  }

  return (
    <div className={`mx-5 my-3 flex items-start gap-3 border rounded-lg px-4 py-3 ${bannerCls}`}>
      {iconEl}
      <div className="min-w-0">
        <p className="text-sm font-bold uppercase tracking-wide">
          Control Assessment — {verdict}
        </p>
        {reason && (
          <p className="text-xs mt-0.5 leading-relaxed opacity-90">{reason}</p>
        )}
      </div>
    </div>
  );
}

function ConclusionSection({ run, exec, attributes, numeral = 'VI', runId, isLocked, onReload }) {
  // The AI's conclusion, findings and recommendations, and the banner: features/control-testing/report/header.ts.
  const {
    aiConclusion, aiKeyFindings: keyFindings, aiRecommendations: recommendations, aiKeyFindingsText, aiRecommendationsText,
  } = conclusionTexts(run, exec);
  const banner = conclusionBanner(exec);
  const isEffective    = banner.kind === 'effective';
  const isNotEffective = banner.kind === 'not_effective';

  const boxCls = isEffective    ? 'border-green-400 bg-green-50'
               : isNotEffective ? 'border-red-400 bg-red-50'
               :                  'border-amber-400 bg-amber-50';
  const labelCls = isEffective    ? 'text-green-800'
                 : isNotEffective ? 'text-red-900'
                 :                  'text-amber-900';
  const label = banner.label;

  const saveConclusion = async (val) => {
    await axiosInstance.patch(`/api/control-test-runs/${runId}/human-conclusion`, {
      human_conclusion: val || null,
    });
    onReload?.();
  };

  const saveKeyFindings = async (val) => {
    await axiosInstance.patch(`/api/control-test-runs/${runId}/human-key-findings`, {
      human_key_findings: val || null,
    });
    onReload?.();
  };

  const saveRecommendations = async (val) => {
    await axiosInstance.patch(`/api/control-test-runs/${runId}/human-recommendations`, {
      human_recommendations: val || null,
    });
    onReload?.();
  };


  const renderLines = (v) => (
    <ol className="space-y-1.5 list-decimal list-inside">
      {String(v).split('\n').filter(Boolean).map((item, i) => (
        <li key={i} className="text-xs text-gray-700 leading-relaxed">{item}</li>
      ))}
    </ol>
  );

  return (
    <WPSection numeral={numeral} title="Conclusion — Operating Effectiveness">
      <div className="space-y-4">
        {/* Conclusion box */}
        <div className={`border-l-4 px-4 py-3.5 ${boxCls}`}>
          <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${labelCls}`}>{label}</p>
          {runId ? (
            <EditableField
              aiValue={aiConclusion}
              humanValue={run?.human_conclusion || null}
              fieldType="textarea"
              placeholder="Enter audit conclusion…"
              emptyLabel="Conclusion text will appear here after the test run completes."
              onSave={saveConclusion}
              onReset={() => saveConclusion(null)}
              locked={isLocked}
            />
          ) : (
            <p className="text-xs text-gray-800 leading-relaxed whitespace-pre-wrap">
              {run?.human_conclusion || aiConclusion || <span className="text-gray-400 italic">Conclusion text will appear here after the test run completes.</span>}
            </p>
          )}
        </div>

        {/* Key observations */}
        {(aiKeyFindingsText || run?.human_key_findings) && (
          <div>
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-2">Key Observations</p>
            {runId ? (
              <EditableField
                aiValue={aiKeyFindingsText}
                humanValue={run?.human_key_findings || null}
                fieldType="textarea"
                placeholder="Enter key observations…"
                emptyLabel="No key observations recorded."
                renderValue={renderLines}
                onSave={saveKeyFindings}
                onReset={() => saveKeyFindings(null)}
                locked={isLocked}
              />
            ) : (
              <ol className="space-y-1.5 list-decimal list-inside">
                {keyFindings.map((f, i) => (
                  <li key={i} className="text-xs text-gray-700 leading-relaxed">{f}</li>
                ))}
              </ol>
            )}
          </div>
        )}

        {/* Recommendations */}
        {(aiRecommendationsText || run?.human_recommendations) && (
          <div>
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-2">Recommendations</p>
            {runId ? (
              <EditableField
                aiValue={aiRecommendationsText}
                humanValue={run?.human_recommendations || null}
                fieldType="textarea"
                placeholder="Enter recommendations…"
                emptyLabel="No recommendations recorded."
                renderValue={renderLines}
                onSave={saveRecommendations}
                onReset={() => saveRecommendations(null)}
                locked={isLocked}
              />
            ) : (
              <div className="space-y-1.5">
                {recommendations.map((r, i) => (
                  <div key={i} className="flex gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-900 leading-relaxed">{r}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </WPSection>
  );
}

/* ── VII. Exhibits / References ─────────────────────────────────────────────── */
function ExhibitsSection({ evidenceMap, attributes, onFileClick, run, numeral = 'VII' }) {
  // Files, exhibit numbers, attributes and status: features/control-testing/report/exhibits.ts (shared with the
  // working paper).
  const exhibits = buildExhibits(evidenceMap, attributes, run);
  const files = exhibits.files.map((e) => ({
    fileName: e.file, attrRefs: new Set(e.attributes), source: e.source, fileId: e.fileId, locator: e.locator,
  }));
  const fileToExhibit = new Map(exhibits.files.map((e) => [e.file, e.label]));
  const popExtraFiles = exhibits.population.map((e) => ({
    exhibitLabel: e.label, fileName: e.file, fileId: e.fileId, typeLabel: e.type, source: e.source,
  }));

  const handleView = (f) => {
    if (!onFileClick) return;
    const fileRef = f.locator?.file_ref || null;
    if (!fileRef && !f.fileId) return;
    onFileClick({ source_file: f.fileName, source_file_id: f.fileId || null, file_ref: fileRef, excerpt: '', page_number: f.locator?.page || null, row_numbers: null });
  };

  const totalFileCount = popExtraFiles.length + files.length;

  if (totalFileCount === 0) {
    return (
      <WPSection numeral={numeral} title="Exhibits / References">
        <p className="text-xs text-gray-400 italic">No evidence files mapped to this run.</p>
      </WPSection>
    );
  }

  const srcBadge = (src) => {
    if (!src || src === 'missing')   return <span className="text-[10px] text-red-600 border border-red-200 bg-red-50 px-1.5 py-0.5">Missing</span>;
    if (src === 'ai_mapped')         return <span className="text-[10px] text-amber-600 border border-amber-200 bg-amber-50 px-1.5 py-0.5">Incomplete</span>;
    if (src === 'population')        return <span className="text-[10px] text-blue-700 border border-blue-200 bg-blue-50 px-1.5 py-0.5">Population</span>;
    if (src === 'ipe_proof')         return <span className="text-[10px] text-violet-700 border border-violet-200 bg-violet-50 px-1.5 py-0.5">IPE Proof</span>;
    return                                  <span className="text-[10px] text-green-700 border border-green-200 bg-green-50 px-1.5 py-0.5">Available</span>;
  };

  return (
    <WPSection numeral={numeral} title={`Exhibits / References (${totalFileCount} file${totalFileCount !== 1 ? 's' : ''})`} noPad>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-gray-100 border-b-2 border-gray-300">
            <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wide w-12">Exhibit</th>
            <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wide">Evidence File</th>
            <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wide w-28">Attributes</th>
            <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wide w-24">Status</th>
            <th className="px-3 py-2 w-16" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {/* Population and IPE proof files first */}
          {popExtraFiles.map((f, i) => (
            <tr key={`pop-${i}`} className="hover:bg-blue-50/30 bg-blue-50/10">
              <td className="px-3 py-2 font-mono font-bold text-blue-700">{f.exhibitLabel}</td>
              <td className="px-3 py-2">
                <span className="flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 text-blue-400 shrink-0" />
                  <span className="text-gray-800 truncate max-w-xs">{f.fileName}</span>
                </span>
              </td>
              <td className="px-3 py-2">
                <span className="text-[10px] text-blue-700 font-medium">{f.typeLabel}</span>
              </td>
              <td className="px-3 py-2">{srcBadge(f.source)}</td>
              <td className="px-3 py-2">
                <div className="flex items-center gap-1">
                  <button className="p-1 text-gray-400 hover:text-gray-600" title="Download">
                    <Download className="h-3.5 w-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {/* Sample evidence files */}
          {files.map((f, i) => {
            const canView = !!(f.locator?.file_ref || f.fileId);
            const exhibitLabel = fileToExhibit.get(f.fileName) || `E-${i + 1}`;
            return (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-mono font-bold text-gray-600">{exhibitLabel}</td>
                <td className="px-3 py-2">
                  <span className="flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <span className="text-gray-800 truncate max-w-xs">{f.fileName}</span>
                  </span>
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {[...f.attrRefs].map(ref => (
                      <span key={ref} className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-100 border border-slate-300 text-slate-700 text-[9px] font-bold">{ref}</span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2">{srcBadge(f.source)}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    {canView && (
                      <button onClick={() => handleView(f)} className="p-1 text-indigo-400 hover:text-indigo-600" title="View annotated evidence">
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button className="p-1 text-gray-400 hover:text-gray-600" title="Download">
                      <Download className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </WPSection>
  );
}

/* ── VIII. Sign-Off ─────────────────────────────────────────────────────────── */
function SignOffSection({ run, exec, confPct }) {
  const runDate  = run?.created_at ? fmtDate(run.created_at) : '—';
  const signedAt = run?.signed_off_at ? fmtDate(run.signed_off_at) : null;

  return (
    <WPSection numeral="VIII" title="Sign-Off & Review">
      <div className="space-y-4">
        {/* AI analysis row */}
        <div className="border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <Sparkles className="h-3 w-3 text-indigo-400" /> AI-Assisted Analysis
            </span>
          </div>
          <div className="px-4 py-3 flex flex-wrap gap-8">
            <div><p className="text-[10px] text-gray-400 uppercase tracking-wide">Run Date</p><p className="text-xs text-gray-800 mt-0.5">{runDate}</p></div>
            <div><p className="text-[10px] text-gray-400 uppercase tracking-wide">Run No.</p><p className="text-xs font-mono text-gray-800 mt-0.5">RUN-{run?.run_number ?? '?'}</p></div>
            {confPct != null && <div><p className="text-[10px] text-gray-400 uppercase tracking-wide">AI Confidence</p><p className="text-xs text-gray-800 mt-0.5">{confPct}%</p></div>}
            <div><p className="text-[10px] text-gray-400 uppercase tracking-wide">Exceptions</p><p className="text-xs text-gray-800 mt-0.5">{exec?.total_exceptions ?? 0}</p></div>
          </div>
        </div>

        {/* Preparer */}
        <div className="border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1.5"><PenLine className="h-3 w-3" />Preparer Sign-Off</span>
          </div>
          <div className="px-4 py-3 flex items-end gap-10">
            <div className="flex-1 max-w-xs">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1.5">Name / Signature</p>
              <div className="border-b-2 border-gray-400 h-6 w-full" />
            </div>
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1.5">Date</p>
              <div className="border-b-2 border-gray-400 h-6 w-32" />
            </div>
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1.5">Initials</p>
              <div className="border-b-2 border-gray-400 h-6 w-16" />
            </div>
          </div>
        </div>

        {/* Reviewer */}
        <div className="border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1.5"><PenLine className="h-3 w-3" />Reviewer Sign-Off</span>
            {signedAt && <span className="text-[10px] text-green-600 font-medium">✓ Signed off {signedAt}</span>}
          </div>
          <div className="px-4 py-3 flex items-end gap-10">
            <div className="flex-1 max-w-xs">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1.5">Name / Signature</p>
              {run?.signed_off_by
                ? <p className="text-sm text-gray-800 font-medium border-b-2 border-green-500 pb-1">{run.signed_off_by}</p>
                : <div className="border-b-2 border-gray-400 h-6 w-full" />
              }
            </div>
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1.5">Date</p>
              {signedAt
                ? <p className="text-xs text-gray-800 border-b-2 border-green-500 pb-1 w-32">{signedAt}</p>
                : <div className="border-b-2 border-gray-400 h-6 w-32" />
              }
            </div>
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1.5">Notes</p>
              <div className="border-b-2 border-gray-400 h-6 w-24" />
            </div>
          </div>
        </div>
      </div>
    </WPSection>
  );
}

/* ── Run history tab ────────────────────────────────────────────────────────── */
function RunHistoryTab({ allRuns }) {
  if (!allRuns?.length) return <div className="py-6 text-sm text-gray-400 italic text-center">No run history available.</div>;
  const conclusionLabel = (exec) => {
    const c = (exec?.conclusion || exec?.overall_status || '').toLowerCase();
    if (c === 'effective')                                     return { text: 'Effective', cls: 'text-green-700 bg-green-50 border-green-200' };
    if (['ineffective','failed','not_effective'].includes(c)) return { text: 'Not Effective', cls: 'text-red-700 bg-red-50 border-red-200' };
    if (c === 'needs_review')                                  return { text: 'Needs Review', cls: 'text-amber-700 bg-amber-50 border-amber-200' };
    return { text: exec?.overall_status || '—', cls: 'text-gray-600 bg-gray-50 border-gray-200' };
  };
  return (
    <div className="space-y-2">
      {allRuns.map((r, i) => {
        const exec = r.execution || {};
        const m = conclusionLabel(exec);
        return (
          <div key={r.id || i} className="border border-gray-100 rounded px-4 py-3 bg-white flex items-center gap-3">
            <span className="font-mono text-xs text-gray-400">RUN-{r.run_number || i + 1}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded border ${m.cls}`}>{m.text}</span>
            <span className="text-gray-500 text-xs">{fmt(r.testing_period_start)} — {fmt(r.testing_period_end)}</span>
            {exec.confidence_score != null && (
              <span className="ml-auto text-xs text-indigo-500 flex items-center gap-1"><Sparkles className="h-3 w-3" />{Math.round(exec.confidence_score * 100)}%</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════════════════════════ */
export default function AttributeResultPage({ embeddedRunId, onSignOff, sectionFilter } = {}) {
  const { selectedAuditYearId } = useAuditYear();
  const { id }           = useParams();
  const navigate         = useNavigate();
  const [searchParams]   = useSearchParams();
  const runIdFromUrl     = embeddedRunId || searchParams.get('run');
  const embedded         = !!embeddedRunId;
  const {
    checkAccess: checkTestExecutionsModuleAccess,
    dialogProps: testExecutionsModuleAccessDialogProps,
  } = useRequireModuleAccess(MODULE_KEYS.TEST_EXECUTIONS, {
    hint:
      'Running or editing attribute test results requires the Test Executions module. Contact your organization administrator if you need access.',
  });
  const testExecutionAccessValue = useMemo(
    () => ({ checkAccess: checkTestExecutionsModuleAccess }),
    [checkTestExecutionsModuleAccess]
  );

  const [control, setControl]           = useState(null);
  const [run, setRun]                   = useState(null);
  const [attributes, setAttributes]     = useState([]);
  const [planFields, setPlanFields]     = useState([]);
  const [livePlanFields, setLivePlanFields] = useState([]);
  const [planAttributes, setPlanAttributes] = useState([]);
  const [evidenceMap, setEvidenceMap]   = useState([]);
  const [allRuns, setAllRuns]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [running, setRunning]           = useState(false);
  const [progress, setProgress]         = useState(null);
  const [error, setError]               = useState(null);
  const [activeTab, setActiveTab]       = useState('history');
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [watermark, setWatermark]       = useState(null); // null | 'draft' | 'final'
  const abortRef = useRef(null);

  /* Evidence viewer state */
  const [viewerOpen, setViewerOpen]       = useState(false);
  const [viewerCitation, setViewerCitation] = useState(null);
  const [viewerFindings, setViewerFindings] = useState(null);
  const [exceptionsKey, setExceptionsKey] = useState(0);

  const openEvidenceViewer = useCallback((citation, findings = null) => {
    setViewerCitation(citation);
    setViewerFindings(findings);
    setViewerOpen(true);
  }, []);

  const reloadRun = useCallback(async () => {
    if (!run?.id) return;
    try {
      const detailResp = await axiosInstance.get(`/api/control-test-runs/${run.id}`);
      const fullRun = detailResp.data?.run;
      if (fullRun) {
        setRun(fullRun);
        setAttributes(detailResp.data?.attributes || []);
      }
    } catch {
      // non-fatal refresh
    }
  }, [run?.id]);

  /* Load run data */
  const loadRun = useCallback(async (controlObj, specificRunId = null) => {
    const uuid = controlObj?.id;
    if (!uuid) return;
    try {
      let runId;
      if (specificRunId) {
        runId = specificRunId;
      } else {
        const latestResp = await axiosInstance.get(`/api/control-test-runs/control/${uuid}/latest`);
        const runHeader  = latestResp.data?.run;
        if (!runHeader) { setRun(null); setAttributes([]); return; }
        runId = runHeader.id;
      }

      const detailResp = await axiosInstance.get(`/api/control-test-runs/${runId}`);
      const fullRun    = detailResp.data?.run;
      if (!fullRun) { setRun(null); setAttributes([]); return; }
      setRun(fullRun);
      setAttributes(detailResp.data?.attributes || []);

      try {
        const emResp = await axiosInstance.get(`/api/control-test-runs/${runId}/evidence-map`);
        setEvidenceMap(emResp.data?.cells || []);
      } catch { setEvidenceMap([]); }

      try {
        const allResp = await axiosInstance.get(`/api/control-test-runs/control/${uuid}`);
        setAllRuns(allResp.data?.runs || []);
      } catch { setAllRuns([]); }
    } catch { setRun(null); setAttributes([]); }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // When embedded (no route :id param), derive sox_control_id from the run first.
        let controlId = id;
        if (!controlId && runIdFromUrl) {
          const runResp = await axiosInstance.get(`/api/control-test-runs/${runIdFromUrl}`);
          if (cancelled) return;
          controlId = runResp.data?.run?.sox_control_id;
        }
        if (!controlId) { if (!cancelled) setError('Control not found.'); return; }

        const resp = await axiosInstance.get(`/api/sox-controls/${controlId}`);
        if (cancelled) return;
        const ctrl = resp.data;
        setControl(ctrl);

        await Promise.all([
          loadRun(ctrl, runIdFromUrl),
          (async () => {
            try {
              const planResp = await (runIdFromUrl
                ? axiosInstance.get(`/api/control-test-runs/${runIdFromUrl}/plan`)
                : axiosInstance.get(`/api/sox-controls/${ctrl.id}/test-plan`)
              );
              const snapFields = planResp.data?.fields || [];

              // When viewing a specific run, also load the LIVE plan so we can
              // overlay current field labels onto the frozen snapshot.  The snapshot
              // preserves audit-trail field_keys / behaviour; the live plan gives us
              // the labels the user currently recognises (they may have been renamed
              // since the run was created).
              let liveFields = [];
              if (runIdFromUrl && ctrl.id) {
                try {
                  const liveResp = await axiosInstance.get(`/api/sox-controls/${ctrl.id}/test-plan`);
                  liveFields = liveResp.data?.fields || [];
                } catch { /* non-fatal */ }
              }

              if (!cancelled) {
                // Keep snapshot fields as planFields — they carry the exact field_key /
                // label the AI used for extraction, so lookupField() works perfectly.
                // Store live plan fields separately so SampleDataTable can overlay the
                // human-configured labels onto the column headers where they match.
                setPlanFields(snapFields.length > 0 ? snapFields : liveFields);
                setLivePlanFields(liveFields);
                setPlanAttributes(planResp.data?.attributes || []);
              }
            } catch { /* ignore plan load failure */ }
          })(),
        ]);
      } catch {
        if (!cancelled) setError('Failed to load control or test run.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, loadRun, runIdFromUrl]);

  const handleRun = async () => {
    if (embedded) return;
    if (!control?.id) return;
    if (!checkTestExecutionsModuleAccess()) return;
    setRunning(true);
    setError(null);
    setProgress({ message: 'Starting…' });
    abortRef.current = new AbortController();
    try {
      await runAttributeTest({
        controlUuid: control.id,
        controlId: control.control_id,
        testingPeriodStart: null,
        testingPeriodEnd: null,
        additionalContext: null,
        auditYearId: selectedAuditYearId,
        onProgress: (p) => setProgress(p),
        signal: abortRef.current.signal,
      });
      await loadRun(control);
    } catch (e) {
      setError(resolve403ErrorMessage(e, e.message || 'Attribute test failed.'));
    } finally {
      setRunning(false);
      setProgress(null);
    }
  };

  // The working paper is built from what these tabs show (features/control-testing/report), then laid out.
  const exportWorkingPaper = async (format, setBusy) => {
    if (!run?.id) return;
    setBusy(true);
    try {
      // The plan columns and control this page shows, so the file matches the screen.
      await downloadWorkingPaper(run.id, format, {}, { control, planFields, livePlanFields, planAttributes });
    } catch (e) {
      const what = format === 'pdf' ? 'PDF' : 'Excel';
      toast.error(`${what} export failed: ` + resolve403ErrorMessage(e, await blobErrorMessage(e)));
    } finally {
      setBusy(false);
    }
  };
  const handleExportPdf = () => exportWorkingPaper('pdf', setExportingPdf);
  const handleExportExcel = () => exportWorkingPaper('excel', setExportingExcel);

  const handleWatermarkToggle = () => {
    setWatermark(w => w == null ? 'draft' : w === 'draft' ? 'final' : null);
  };

  /* Derived */
  const exec     = run?.execution || {};
  const confPct  = confidencePct(exec);
  const isLocked = run?.workflow_status === 'signed_off';

  const allSampleRefs = [...new Set(
    attributes.flatMap(a => (a.sample_results || []).map(sr => norm(sr.record_ref)))
  )];
  const { reviewed: reviewedCount } = reviewProgress(attributes);

  const withTestExecutionAccess = (content) => (
    <TestExecutionAccessContext.Provider value={testExecutionAccessValue}>
      {content}
      <ModuleAccessDeniedDialog {...testExecutionsModuleAccessDialogProps} />
    </TestExecutionAccessContext.Provider>
  );

  /* ── Loading ── */
  if (loading) {
    return withTestExecutionAccess(
      <div className="flex items-center justify-center py-24 text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading working paper…
      </div>
    );
  }

  /* ── Back button (non-embedded only) ── */
  const backBtn = !embedded && (
    <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3">
      <ArrowLeft className="h-4 w-4" /> Back
    </button>
  );

  /* ── No run yet ── */
  if (!run && !running) {
    return withTestExecutionAccess(
      <div className={embedded ? '' : 'px-5 py-3 max-w-5xl mx-auto'}>
        {backBtn}
        {control && (
          <WPHeader control={control} run={null} exec={{}} confPct={null} running={false} onRerun={handleRun} embedded={embedded} />
        )}
        <div className="mt-4 border border-dashed border-gray-200 rounded-xl p-14 text-center bg-white">
          <Play className="h-8 w-8 mx-auto text-gray-300 mb-3" />
          <p className="text-sm font-medium text-gray-600 mb-1">No attribute test run yet</p>
          <p className="text-xs text-gray-400 mb-4">Run the attribute test to populate this working paper.</p>
          <button onClick={handleRun} className="inline-flex items-center gap-1.5 text-sm bg-slate-700 text-white px-4 py-2 rounded hover:bg-slate-800">
            <Play className="h-4 w-4" /> Run Attribute Test
          </button>
        </div>
      </div>
    );
  }

  /* ── Population-only mode (used by Population C/A tab in modal) ── */
  if (sectionFilter === 'population') {
    return withTestExecutionAccess(
      <div>
        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-4 py-2.5 mb-3">{error}</div>
        )}
        <PopulationSection
          run={run}
          planFields={planFields}
          evidenceMap={evidenceMap}
          attributes={attributes}
          runId={run?.id}
          onReload={reloadRun}
          isLocked={run?.workflow_status === 'signed_off'}
          populationRationale={getPopulationRequirement(control).rationale}
        />
      </div>
    );
  }

  if (sectionFilter === 'samples') {
    return withTestExecutionAccess(
      <div>
        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-4 py-2.5 mb-3">{error}</div>
        )}
        <AttributeTestingTableSection
          attributes={attributes}
          planFields={planFields}
          livePlanFields={livePlanFields}
          evidenceMap={evidenceMap}
          run={run}
          runId={runIdFromUrl}
          onAfterOverride={() => { setExceptionsKey(k => k + 1); reloadRun(); }}
        />
      </div>
    );
  }

  return withTestExecutionAccess(
    <div className={`space-y-3 ${embedded ? '' : 'px-5 py-3 max-w-5xl mx-auto'}`}>
      {backBtn}

      {/* Progress / error banners */}
      {running && progress && (
        <div className="text-sm text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-4 py-2.5 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          {progress.message || 'Working…'}
          {typeof progress.progress === 'number' && progress.progress >= 0 && <span>· {progress.progress}%</span>}
        </div>
      )}
      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-4 py-2.5">{error}</div>
      )}

      {/* Watermark overlay */}
      {watermark && (
        <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center overflow-hidden">
          <span
            className="select-none font-black text-[110px] tracking-widest uppercase opacity-[0.07]"
            style={{ transform: 'rotate(-35deg)', color: watermark === 'draft' ? '#B45309' : '#15803D' }}
          >
            {watermark}
          </span>
        </div>
      )}

      {/* ══ WORKING PAPER ══ */}
      <WPHeader
        control={control}
        run={run}
        exec={exec}
        confPct={confPct}
        running={running}
        onRerun={handleRun}
        embedded={embedded}
        onExportPdf={handleExportPdf}
        exportingPdf={exportingPdf}
        onExportExcel={handleExportExcel}
        exportingExcel={exportingExcel}
        watermark={watermark}
        onWatermarkToggle={handleWatermarkToggle}
        reviewedCount={reviewedCount}
        totalSamples={allSampleRefs.length}
        preparerInfo={attributes[0]?.preparer_info}
      />

      {/* F2 — verdict banner: Control Assessment — hidden per feedback (keep code, don't delete) */}
      {/* {!running && attributes.length > 0 && (
        <VerdictBanner run={run} exec={exec} attributes={attributes} />
      )} */}

      {control && <ObjectiveSection control={control} run={run} />}

      {/* PopulationSection (WPSection II) moved to Population C/A tab — not shown in Summary Results */}
      {/* Sections IV & V moved to Samples Testing / Issues tabs — visible sections renumbered I → II → III → IV */}

      <WorkPerformedSection
        attributes={attributes}
        planAttributes={planAttributes}
        numeral="II"
        runId={run?.id}
        isLocked={isLocked}
        onReload={reloadRun}
      />

      <ConclusionSection
        run={run}
        exec={exec}
        attributes={attributes}
        numeral="III"
        runId={run?.id}
        isLocked={isLocked}
        onReload={reloadRun}
      />

      <ExhibitsSection
        numeral="IV"
        evidenceMap={evidenceMap}
        attributes={attributes}
        run={run}
        onFileClick={(citation) => {
          const allFindings = {
            findings_summary: attributes
              .filter(a => a.findings_summary)
              .map(a => `[${a.attribute_ref}] ${a.findings_summary}`)
              .join('\n\n'),
            exceptions: attributes.flatMap(a => a.exceptions || []),
          };
          openEvidenceViewer(citation, allFindings);
        }}
      />

      <WPSection numeral="V" title="Sign-Off & Review" noPad>
        <div className="px-5 py-4">
          <SignOffPanel run={run} refreshKey={exceptionsKey} onSigned={async () => { await reloadRun(); onSignOff?.(); }} />
        </div>
      </WPSection>

      {/* Run History collapsible at bottom */}
      <WPSection numeral="" title={`Run History (${allRuns.length})`} defaultOpen={false}>
        <RunHistoryTab allRuns={allRuns} />
      </WPSection>

      {/* Evidence viewer modal */}
      <EvidenceViewer
        citation={viewerCitation}
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        stepFindings={viewerFindings}
        executionId={run?.id}
      />
    </div>
  );
}
